import { useCallback, useMemo, useState } from 'react'

import type { ColumnView } from '../../../../domain'

import type { SubscriptionFilterArgs } from '../../../../application'

import { useAppUsecase } from '../../State'

const filterKinds = ['connection', 'server', 'service'] as const

/**
 * One view per list and variant, whichever resource the list is filtered by.
 * The kind of filter is part of the variant, as it changes the columns (e.g. endpoints under a connection have no connection column),
 * and saving a view drops the names that aren't columns of the table it was saved from
 */
const toColumnViewId = (subscriptionUsecaseName: string, short: boolean, filter: SubscriptionFilterArgs): string =>
    [subscriptionUsecaseName, short ? 'short' : 'full', ...filterKinds.filter((kind) => Boolean(filter[kind]))].join(':')

type ChangedColumnView = Readonly<{ id: string, view: ColumnView }>

type UsedColumnView = Readonly<{ columnView: ColumnView, onColumnViewChange: (view: ColumnView) => void }>

export const useColumnView = (subscriptionUsecaseName: string, short: boolean, filter: SubscriptionFilterArgs): UsedColumnView => {
    const tableColumnViewUsecase = useAppUsecase('tableColumnViewUsecase')
    const id = toColumnViewId(subscriptionUsecaseName, short, filter)

    const saved = useMemo(() => tableColumnViewUsecase.get(id), [tableColumnViewUsecase, id])
    const [changed, setChanged] = useState<ChangedColumnView | null>(null)

    const onColumnViewChange = useCallback(
        (view: ColumnView) => {
            setChanged({ id, view })
            tableColumnViewUsecase.save(id, view)
        },
        [tableColumnViewUsecase, id]
    )

    return { columnView: changed?.id === id ? changed.view : saved, onColumnViewChange }
}
